import {
  Banknote,
  BriefcaseBusiness,
  Building2,
  ChartBar,
  Contact,
  FileSignature,
  FolderKanban,
  HandCoins,
  House,
  IdCard,
  Inbox,
  LayoutGrid,
  Magnet,
  Megaphone,
  MessageSquare,
  ReceiptText,
  ScrollText,
  Stamp,
  Timer,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type { ComponentType } from "react";

import type { Feature } from "@/app/session/features";
import { TEAM_SECTION_PERMISSIONS, type Permission } from "@/app/session/permissions";
import { RequestsNavBadge } from "@/features/requests/components/RequestsNavBadge";
import type { MessageId } from "@/shared/i18n/messages";

export interface NavEntry {
  /** Stable id: the React key and the command palette's search value. */
  id: string;
  to: string;
  labelId: MessageId;
  icon: LucideIcon;
  /** Shown only to people with this permission, or any of these (see useCan). */
  permission?: Permission | readonly Permission[];
  /** Shown only while this product area is switched on (see useFeature). */
  feature?: Feature;
  /** An employee's own page: shown to people with an employee record, or with hr.view. */
  forEmployees?: boolean;
  /** Active only on the exact path, not on the pages under it. */
  end?: boolean;
  /** Extra command-palette words (the label is always searchable). */
  keywordIds?: readonly MessageId[];
  /** A live count after the label in the sidebar, such as the requests waiting for me. */
  badge?: ComponentType;
  /**
   * The sidebar section it sits in: the daily work, the agency around it, its people, sales, or
   * the footer listed after every section without a heading.
   */
  group: NavGroup | "footer";
}

export type NavGroup = "work" | "agency" | "people" | "sales";

/** Sidebar sections, in order, with their headings. The footer has no heading. */
export const NAV_GROUPS: readonly { id: NavGroup; labelId: MessageId }[] = [
  { id: "work", labelId: "nav.group.work" },
  { id: "agency", labelId: "nav.group.agency" },
  { id: "people", labelId: "hr.nav.group" },
  { id: "sales", labelId: "nav.group.sales" },
];

const SALES_KEYWORDS = ["billing.nav", "nav.group.sales"] as const satisfies readonly MessageId[];
const HR_KEYWORDS = ["hr.nav.group"] as const satisfies readonly MessageId[];

/**
 * The app's main sections, in order. The sidebar (desktop and phone) and the command palette's
 * "Go to" group are built from this list. Only sections with a route belong here.
 */
export const NAV_ENTRIES: readonly NavEntry[] = [
  { id: "home", group: "work", to: "/", labelId: "home.nav", icon: House, end: true },
  {
    id: "leads",
    group: "work",
    to: "/leads",
    labelId: "leads.nav",
    icon: Magnet,
    end: true,
    permission: "leads.manage",
  },
  {
    id: "tickets",
    group: "footer",
    to: "/tickets",
    labelId: "tickets.nav",
    icon: MessageSquare,
    permission: "tickets.view",
  },
  { id: "my-work", group: "work", to: "/my-work", labelId: "nav.myWork", icon: Inbox, end: true },
  {
    id: "projects",
    group: "work",
    to: "/projects",
    labelId: "nav.projects",
    icon: FolderKanban,
    end: true,
  },
  {
    id: "all-tasks",
    group: "work",
    to: "/tasks",
    labelId: "allTasks.nav",
    icon: LayoutGrid,
    end: true,
    permission: "projects.view_all",
    keywordIds: ["allTasks.title"],
  },
  { id: "time", group: "work", to: "/time", labelId: "time.nav", icon: Timer, end: true },
  {
    id: "reports",
    group: "agency",
    to: "/reports/time",
    labelId: "time.reports.nav",
    icon: ChartBar,
  },
  {
    id: "team",
    group: "agency",
    to: "/team",
    labelId: "team.title",
    icon: Users,
    permission: TEAM_SECTION_PERMISSIONS,
  },
  {
    id: "clients",
    group: "agency",
    to: "/clients",
    labelId: "nav.clients",
    icon: Building2,
    permission: "clients.view",
  },
  {
    id: "my-hr",
    group: "people",
    to: "/me/hr",
    labelId: "hr.nav.myHr",
    icon: IdCard,
    end: true,
    feature: "people",
    forEmployees: true,
    keywordIds: HR_KEYWORDS,
  },
  {
    id: "people",
    group: "people",
    to: "/people",
    labelId: "hr.nav.people",
    icon: Contact,
    permission: "hr.view",
    feature: "people",
    keywordIds: [...HR_KEYWORDS, "hr.tabs.directory", "hr.tabs.orgChart"],
  },
  {
    id: "requests",
    group: "people",
    to: "/requests",
    labelId: "hr.nav.requests",
    icon: Stamp,
    feature: "people",
    forEmployees: true,
    keywordIds: HR_KEYWORDS,
    badge: RequestsNavBadge,
  },
  {
    id: "company",
    group: "people",
    to: "/company",
    labelId: "hr.nav.company",
    icon: Megaphone,
    feature: "engagement",
    forEmployees: true,
    keywordIds: HR_KEYWORDS,
  },
  {
    id: "hiring",
    group: "people",
    to: "/hiring",
    labelId: "hr.nav.hiring",
    icon: BriefcaseBusiness,
    permission: "recruitment.view",
    feature: "recruitment",
    keywordIds: HR_KEYWORDS,
  },
  {
    id: "payroll",
    group: "people",
    to: "/payroll",
    labelId: "hr.nav.payroll",
    icon: HandCoins,
    permission: "payroll.view",
    feature: "payroll",
    keywordIds: HR_KEYWORDS,
  },
  {
    id: "quotes",
    group: "sales",
    to: "/billing/quotes",
    labelId: "billing.tab.quotes",
    icon: FileSignature,
    permission: "billing.view",
    keywordIds: SALES_KEYWORDS,
  },
  {
    id: "contracts",
    group: "sales",
    to: "/billing/contracts",
    labelId: "billing.tab.contracts",
    icon: ScrollText,
    permission: "billing.view",
    keywordIds: SALES_KEYWORDS,
  },
  {
    id: "invoices",
    group: "sales",
    to: "/billing/invoices",
    labelId: "billing.tab.invoices",
    icon: ReceiptText,
    permission: "billing.view",
    keywordIds: SALES_KEYWORDS,
  },
  {
    id: "payments",
    group: "sales",
    to: "/billing/payments",
    labelId: "billing.tab.payments",
    icon: Banknote,
    permission: "billing.view",
    keywordIds: SALES_KEYWORDS,
  },
  {
    id: "expenses",
    group: "sales",
    to: "/billing/expenses",
    labelId: "billing.tab.expenses",
    icon: Wallet,
    permission: "billing.view",
    keywordIds: SALES_KEYWORDS,
  },
];
